import React from 'react';
import { Card, CardContent } from '../../../../components/ui/Card';
import AppIcon from '../../../../components/AppIcon';
import { cn } from '../../../../utils/cn';
import type { ProgressIndicatorProps } from '../types';

const getProgressColorClass = (value: number): string => {
  if (value >= 80) return 'bg-emerald-500';
  if (value >= 60) return 'bg-blue-500';
  if (value >= 40) return 'bg-amber-500';
  return 'bg-red-500';
};

const ProgressIndicator: React.FC<ProgressIndicatorProps> = ({ indicator }) => {
  return (
    <Card className="h-full">
      <CardContent>
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center shrink-0">
              <AppIcon name={indicator.icon} size={20} className="text-foreground" />
            </div>
            <div>
              <p className="text-sm font-medium text-foreground">{indicator.title}</p>
              <p className="text-xs text-muted-foreground">{indicator.subtitle}</p>
            </div>
          </div>
          <div className="text-end">
            <p className="text-lg font-bold text-foreground">{indicator.value}%</p>
            <p className="text-xs text-muted-foreground">{indicator.period}</p>
          </div>
        </div>

        <div className="h-2 bg-muted rounded-full overflow-hidden">
          <div
            className={cn('h-full rounded-full transition-all duration-500', getProgressColorClass(indicator.value || 0))}
            style={{ width: `${Math.min(Math.max(indicator.value || 0, 0), 100)}%` }}
          />
        </div>

        <div className="flex items-center justify-between mt-2">
          <p className="text-xs text-muted-foreground">{indicator.description}</p>
          <div className="flex items-center gap-1">
            {indicator.trend === 'up' && (
              <AppIcon name="TrendingUp" size={14} className="text-emerald-500" />
            )}
            {indicator.trend === 'down' && (
              <AppIcon name="TrendingDown" size={14} className="text-red-500" />
            )}
            {indicator.trend === 'stable' && (
              <AppIcon name="Minus" size={14} className="text-muted-foreground" />
            )}
            <span
              className={cn(
                'text-xs',
                indicator.trend === 'up' && 'text-emerald-500',
                indicator.trend === 'down' && 'text-red-500',
                indicator.trend === 'stable' && 'text-muted-foreground'
              )}
            >
              {indicator.change}
            </span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default ProgressIndicator;
